import { useMemo, useEffect } from 'react';
import { useDesignerApplicationContext } from './designer-application-context';
import { useUserContext } from './userContext';
import { useGetDesignerStepStatusQuery } from '../hooks/useGetDesignerStepStatusQuery';
import { useGetDesignerOnboardingStatusQuery } from '../hooks/useGetDesignerOnboardingStatusQuery';
import { DesignerApplicationSteps } from '../__generated__/types';
import { FeatureFlagKey } from '../contexts/FeatureFlagsContext';
import { usePaymentSettings } from '../contexts/PaymentSettingsContext';

const normalizeSubscriptionToken = (value: unknown): string => {
  if (typeof value === "string") {
    return value.trim().toLowerCase();
  }

  if (value && typeof value === "object") {
    const wrapped = value as
      | { designerValue?: unknown; homeownerValue?: unknown; value?: unknown; tier?: unknown }
      | null;
    const candidate =
      wrapped?.designerValue ??
      wrapped?.homeownerValue ??
      wrapped?.value ??
      wrapped?.tier;
    return typeof candidate === "string" ? candidate.trim().toLowerCase() : "";
  }

  return "";
};

export interface OnboardingAccessControl {
  canAccessFeature: (featureKey: FeatureFlagKey) => boolean;
  isMatchingProfileUnlocked: boolean;
  isBioUnlocked: boolean;
  isDesignerFilesUnlocked: boolean;
  isOnboardingComplete: boolean;
  currentOnboardingStep: 'payment' | 'matching-profile' | 'bio' | 'designer-files' | 'complete';
  onboardingStatus?: {
    matchingProfileComplete: boolean;
    bioComplete: boolean;
    designFilesComplete: boolean;
  };
  loading: boolean;
}

export const useDesignerOnboardingAccess = (): OnboardingAccessControl => {
  const { user } = useUserContext();
  const { application } = useDesignerApplicationContext();
  const { isPaymentEnabled, isTestMode } = usePaymentSettings();
  const { stepStatus: designerStepStatus } = useGetDesignerStepStatusQuery(user?.id || "");
  const { getDesignerOnboardingStatus, onboardingStatus, loading: onboardingStatusLoading } = useGetDesignerOnboardingStatusQuery(user?.id);
  
  // Fetch onboarding status when user ID is available or when onboarding status changes
  // This ensures the sidebar updates properly after registration completion
  useEffect(() => {
    if (user?.id && user?.accountType === 'Designer') {
      // Always fetch if we don't have data yet
      if (!onboardingStatus) {
        console.log('🔍 useDesignerOnboardingAccess: Initial fetch for user:', user.id);
        getDesignerOnboardingStatus(user.id, false).then(result => {
          console.log('🔍 useDesignerOnboardingAccess: Query result:', result);
        }).catch(err => {
          console.error('❌ Error fetching onboarding status:', err);
        });
      }
      // Refetch when user onboarding status changes (e.g., after registration completion)
      // This fixes the sidebar caching issue where Matches/Messages appear enabled incorrectly
      else {
        const userOnboardingStatus = (user as any)?.onboardingStatus;
        console.log('🔄 useDesignerOnboardingAccess: User onboarding status changed, refetching...', {
          userOnboardingStatus,
          currentCachedStatus: onboardingStatus
        });
        // Trigger a fresh fetch from the network
        getDesignerOnboardingStatus(user.id, true).then(result => {
          console.log('🔄 useDesignerOnboardingAccess: Refreshed query result:', result);
        }).catch(err => {
          console.error('❌ Error refreshing onboarding status:', err);
        });
      }
    }
  }, [user?.id, user?.accountType, (user as any)?.onboardingStatus, getDesignerOnboardingStatus]); // Include onboardingStatus to trigger refresh

  const result = useMemo(() => {
    // Only apply onboarding restrictions to designers
    if (user?.accountType !== 'Designer') {
      return {
        canAccessFeature: (): boolean => true,
        isMatchingProfileUnlocked: true,
        isBioUnlocked: true,
        isDesignerFilesUnlocked: true,
        isOnboardingComplete: true,
        currentOnboardingStep: 'complete' as const,
        loading: false
      };
    }

    // If onboarding status is still loading, return LOCKED defaults to prevent incorrect access
    // This prevents the sidebar from showing Matches/Messages as enabled before data loads
    if (onboardingStatusLoading && !onboardingStatus) {
      // For new users or during initial load, lock everything until we have real data
      const isNewUser = (user as any)?.onboardingStatus === 'pending_profile_completion' ||
                        (user as any)?.onboardingStatus === 'pending_email_verification';
      
      return {
        canAccessFeature: () => !isNewUser, // Lock for new users
        isMatchingProfileUnlocked: !isNewUser,
        isBioUnlocked: false,
        isDesignerFilesUnlocked: false,
        isOnboardingComplete: false,
        currentOnboardingStep: 'matching-profile' as const,
        loading: true
      };
    }

    // Check onboarding status
    const applicationStatus = application?.status;
    const stepCompletion = designerStepStatus?.profile?.stepCompletion;
    const hasCompletedDesignerSteps = !!(stepCompletion && stepCompletion.personalityCompleted && stepCompletion.expertiseCompleted && stepCompletion.styleCompleted && stepCompletion.workStyleCompleted && stepCompletion.preferencesCompleted);
    const hasMatchingProfile = onboardingStatus?.matchingProfileComplete || hasCompletedDesignerSteps;
    const hasBio = onboardingStatus?.bioComplete || false;
    const hasDesignerFiles = onboardingStatus?.designFilesComplete || false;

    const userOnboardingStatus = (user as any)?.onboardingStatus;
    const paymentStatus = (user as any)?.paymentStatus;
    const subscription = (user as any)?.subscription;
    const normalizedSubscription = normalizeSubscriptionToken(subscription);
    const subscriptionToken = normalizedSubscription.replace(/[\s_-]+/g, "");
    const hasPaidMembershipFromSubscription =
      Boolean(subscriptionToken) &&
      !["free", "basic", "home", "homemonthly", "homebiannually"].includes(
        subscriptionToken
      );
    const hasTrustedMembershipEvidence =
      applicationStatus === DesignerApplicationSteps.MembershipActive ||
      userOnboardingStatus === "active" ||
      paymentStatus === "paid" ||
      hasPaidMembershipFromSubscription;

    // Check if we're in beta mode (payment bypass)
    const isBetaMode = !isPaymentEnabled() || isTestMode();
    
    // In beta mode, we bypass payment but still require application approval
    // Only grant full access if user is truly active (admin approved) or has completed payment
    const membershipActiveFromUser =
      userOnboardingStatus === 'active' ||
      userOnboardingStatus === 'matching_profile_completed' ||
      paymentStatus === 'paid' ||
      hasPaidMembershipFromSubscription ||
      hasCompletedDesignerSteps;
    
    // For Matching Profile, Matches, and Messages: Only unlock when application is MembershipActive
    // They should remain LOCKED for: ToBeCompleted (Account Created), CompleteApplication, UnderReview statuses
    // New accounts with pending_profile_completion should NOT have direct access to matching profile
    // They should only access it through the onboarding application flow
    const isMembershipActive =
      applicationStatus === DesignerApplicationSteps.MembershipActive ||
      (applicationStatus === DesignerApplicationSteps.Approved &&
        hasTrustedMembershipEvidence) ||
      membershipActiveFromUser;
    
    // Matching Profile should be unlocked ONLY when:
    // 1. User has already completed the matching profile (can always retake it)
    // 2. Application is fully active (MembershipActive status)
    // 
    // NEW ACCOUNTS with pending_profile_completion should NOT have sidebar access to matching profile
    // They should only access it through the guided onboarding flow on Your Hub
    const isMatchingProfileUnlocked = hasMatchingProfile || isMembershipActive;
    
    // Debug logging
    console.log('🔍 useDesignerOnboardingAccess debug:', {
      applicationStatus,
      onboardingStatus,
      hasMatchingProfile,
      hasBio,
      hasDesignerFiles,
      userAccountType: user?.accountType,
      membershipActiveFromUser,
      userOnboardingStatus,
      paymentStatus,
      subscription,
      normalizedSubscription,
      hasPaidMembershipFromSubscription,
      hasTrustedMembershipEvidence,
      isBetaMode,
      isMembershipActive,
      isMatchingProfileUnlocked,
      isBioUnlocked: isMembershipActive && hasMatchingProfile,
      isDesignerFilesUnlocked: isMembershipActive && hasMatchingProfile
    });
    // Bio should be unlocked if:
    // 1. User has membership active AND matching profile complete (first-time access), OR
    // 2. User has already completed their bio (can always edit it)
    const isBioUnlocked = (isMembershipActive && hasMatchingProfile) || hasBio;
    
    // Designer Files should unlock once matching profile is complete (not requiring bio)
    // Users should be able to upload files while working on their bio
    const isDesignerFilesUnlocked = isMembershipActive && hasMatchingProfile;
    const isOnboardingComplete = isMembershipActive && hasMatchingProfile && hasBio && hasDesignerFiles;

    // Determine current step
    let currentOnboardingStep: 'payment' | 'matching-profile' | 'bio' | 'designer-files' | 'complete';
    if (!isMembershipActive) {
      // In beta mode, if user has pending_profile_completion, they should go to application form
      if (isBetaMode && userOnboardingStatus === 'pending_profile_completion') {
        currentOnboardingStep = 'matching-profile'; // This will show the application form
      } else {
        currentOnboardingStep = 'payment';
      }
    } else if (!hasMatchingProfile) {
      currentOnboardingStep = 'matching-profile';
    } else if (!hasBio) {
      currentOnboardingStep = 'bio';
    } else if (!hasDesignerFiles) {
      currentOnboardingStep = 'designer-files';
    } else {
      currentOnboardingStep = 'complete';
    }

    // Feature access control
    const canAccessFeature = (featureKey: FeatureFlagKey): boolean => {
      // Always accessible features during onboarding
      const alwaysAccessible: FeatureFlagKey[] = [];

      if (alwaysAccessible.includes(featureKey)) {
        return true;
      }

      // Onboarding features - unlock progressively
      if (featureKey === 'matching-profile') {
        return isMatchingProfileUnlocked;
      }

      if (featureKey === 'my-bio') {
        return isBioUnlocked;
      }

      if (featureKey === 'designer-files') {
        return isDesignerFilesUnlocked;
      }

      // All other features require complete onboarding
      return isOnboardingComplete;
    };

    return {
      canAccessFeature,
      isMatchingProfileUnlocked,
      isBioUnlocked,
      isDesignerFilesUnlocked,
      isOnboardingComplete,
      currentOnboardingStep,
      onboardingStatus,
      loading: false
    };
  }, [
    user?.id,
    user?.accountType,
    (user as any)?.onboardingStatus,
    (user as any)?.paymentStatus,
    (user as any)?.subscription,
    application?.status,
    onboardingStatus,
    onboardingStatusLoading,
    designerStepStatus,
  ]);

  return result;
};
